/*
 * Copyright (C) 2020-2024 The FrontDOOR Corp, Inc. All Rights Reserved.
 *
 * This software is the confidential and proprietary information of The FrontDOOR Corp, Inc.
 * ("Confidential Information").  You shall not disclose such Confidential Information and
 * shall use it only in accordance with the terms of the license agreement you entered into
 * with The FrontDOOR Corp, Inc.
 */

/**
 * Grid component to display metrices of a tripId.
 *
 * @version 1.0 26th Feb 2024
 * @author gbiswajeeta
 */
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Grid, IconButton, Typography } from "@mui/material";
import { differenceInMinutes, format, parse, parseISO } from "date-fns";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo, useState } from "react";
import { DdRouteDashboardResponse } from "../../../../../app/models/responseModels/DdRouteResponse";
import {
  DD_ROUTE_PER_STORE_COLUMN_NAME,
  DD_ROUTE_PER_STORE_LABEL_NAME,
  RouteMetricsArgs,
} from "../../../DdTypes";
import { getDefaultMRTOptions } from "../../../DefaultMrtOptions";

// component is to dispalay route metrices of a route
const RouteMetrics = (props: RouteMetricsArgs) => {
  // import base configuration of a grid
  const defaultMRTOptions = getDefaultMRTOptions<DdRouteDashboardResponse>();

  // flag to show loader when component is busy making server calls
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // column configuration for grid
  const columns = useMemo<MRT_ColumnDef<DdRouteDashboardResponse>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        Cell: ({ cell, row }) =>
          new Date(cell.getValue<string>()).toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          }),
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.FDCSTOREID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.FDCSTOREID,
        Cell: ({ cell, row }) => row.original.store.storeId,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.CLIENTSTOREID,
        header: "Client Store ID",
        Cell: ({ cell, row }) => row.original.store.clientStoreId,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TRIPID,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TRIPID,
      },
      {
        header: DD_ROUTE_PER_STORE_LABEL_NAME.DRIVERID,
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.DRIVERID,
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.DRIVERNAME,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.DRIVERNAME,
      },
      {
        accessorKey: "routeCheckoutOn",
        header: "Route Checkout Time",
        Cell: ({ cell, row }) =>
          row.original.routeCheckoutOn
            ? new Date(cell.getValue<string>()).toLocaleString("en-US", {
                hourCycle: "h12",
              })
            : "",
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TRIPACTUALTIME,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TRIPACTUALTIME,
      },
      {
        header: "Stops Per Hour (SPH)",
        Cell: ({ cell, row }) => {
          const rowData = row.original;

          const deliveryDuration = rowData.tripActualTime / 60;
          const ordersUndeliveredAttempt = rowData.ordersUndeliveredAttempt;
          const ordersDelivered = rowData.ordersDelivered;

          const enrouteToReturn = new Date(rowData.enrouteToReturn);
          const atReturn = new Date(rowData.atReturn);

          let rtsDuration = 0;
          if (!isNaN(enrouteToReturn.getTime()) && !isNaN(atReturn.getTime())) {
            rtsDuration =
              atReturn.getHours() * 60 +
              atReturn.getMinutes() -
              (enrouteToReturn.getHours() * 60 + enrouteToReturn.getMinutes());
          }

          const driverDispatchTime =
            deliveryDuration + rowData.driverLoadTime / 60 + rtsDuration / 60;
          return (
            (ordersDelivered + ordersUndeliveredAttempt) /
            driverDispatchTime
          ).toFixed(3);
        },
      },
      {
        header: "Time At Stops (TAS)",
        Cell: ({ cell, row }) =>
          Math.round(
            (row.original.driverDoorstepTime / row.original.totalOrders) * 1000
          ) / 1000,
      },
      {
        header: "Miles Per Stops (MPS)",
        Cell: ({ cell, row }) => {
          const rowData = row.original;

          const mphAssumption = 30;

          const mileage = rowData.miles;
          const totalOrders = rowData.totalOrders;

          const enrouteToReturn = new Date(rowData.enrouteToReturn);
          const atReturn = new Date(rowData.atReturn);
          let rtsDuration = 0;

          if (!isNaN(enrouteToReturn.getTime()) && !isNaN(atReturn.getTime())) {
            rtsDuration =
              atReturn.getHours() * 60 +
              atReturn.getMinutes() -
              (enrouteToReturn.getHours() * 60 + enrouteToReturn.getMinutes());
          }

          const rtsMileage = mphAssumption * (rtsDuration / 60);
          return ((mileage + rtsMileage) / totalOrders).toFixed(3);
        },
      },
      {
        header: "Loadout Time (Mins)",
        Cell: ({ cell, row }) =>
          differenceInMinutes(
            new Date(row.original.departure),
            new Date(row.original.atPickUp)
          ),
      },
      {
        accessorKey: DD_ROUTE_PER_STORE_COLUMN_NAME.TOTALRTSPACKAGES,
        header: DD_ROUTE_PER_STORE_LABEL_NAME.TOTALRTSPACKAGES,
      },
    ],
    [props.route]
  );

  // grid configuration to show route metrices
  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    columns: columns,
    data: [props.route],
    rowCount: 1,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: false,
      },
    },
    state: {
      showProgressBars: isLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      ...defaultMRTOptions.muiTableBodyRowProps,
      onClick: (e) => {
        console.log(row.original);
      },
    }),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="body1" component="p">
          Total Rows: {1}
        </Typography>
      );
    },
  });

  return (
    <Grid container paddingY={3}>
      <Grid
        container
        item
        justifyContent="space-between"
        alignItems="center"
        paddingBottom={2}
      >
        <Typography variant="h4" component="h1">
          Route Metrics
        </Typography>
        <IconButton onClick={(e) => props.close(false)}>
          <CloseRoundedIcon sx={{ color: "primary.secondary" }} />
        </IconButton>
      </Grid>
      <MaterialReactTable table={table} />
    </Grid>
  );
};
export default RouteMetrics;
